import React, { useCallback, useEffect, useRef, useState } from 'react';

import { PeerType } from '@yandex-360/telemost-sdk/models';
import { MediaLimitationReason } from '@yandex-video-platform/goloom-sdk';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { SELF } from '../../../../../../shared/constants';
import {
    selectHideParticipantName,
    selectIsMeetingFullscreenEnabled,
} from '../../../../../../shared/features/config/selectors';
import { selectIsSharingZoomEnabled, selectIsTestplane } from '../../../../../../shared/features/flags/selectors';
import useShouldShowModerationControls from '../../../../../../shared/features/moderation/hooks/useShouldShowModerationControls';
import { selectHideParticipantsVideo } from '../../../../../../shared/features/settings';
import { isMobileUa } from '../../../../../../shared/features/uatraits/selectors';
import { useTypedSelector } from '../../../../../../shared/hooks/useTypedSelector';
import { useVideoOrientation } from '../../../../../../shared/hooks/useVideoOrientation';
import { ParticipantAvatar } from '../../../../components/participant-avatar';
import { ParticipantName } from '../../../../components/participant-name';
import { ParticipantSharingPreloader } from '../../../../components/participant-sharing-preloader';
import { ParticipantStatuses } from '../../../../components/participant-statuses';
import { ParticipantVideoPreloader } from '../../../../components/participant-video-preloader';
import { GRID_VIEW, N_LAST_OPTION, NetworkQualityType } from '../../../../constants';
import {
    getActiveGridView,
    getDesktopSharingStatus,
    getParticipant,
    getParticipantsCount,
    isSidebarVisible,
} from '../../../../selectors';
import {
    participantScreenSharingSuccess,
    participantTurnedVideoOff,
    participantTurnedVideoOn,
    participantTurnedVideoOnSuccess,
} from '../../../conference/slice';
import { FullscreenButton } from '../../../fullscreen';
import { SuspendedUseZoomPan } from '../../../sharing-zoom/components';
import { SharingControlsPanel } from '../../../sharing-zoom/components/sharing-controls-panel';
import { SharingZoomController } from '../../../sharing-zoom/SharingZoomController';
import { shouldShowFullscreenButton } from './helpers';
import styles from './styles.css';

interface Props {
    /**
     * Идентификатор пользователя
     */
    userId: string;
    /**
     * Флаг о том что пользователь активно говорит
     */
    isVAD?: boolean;
    /**
     * Показывает что эти контролы принадлежат экрану шаринга
     */
    isScreenSharing?: boolean;
    /**
     * Показывает что эти контролы принадлежат слоту с камерой и камера включена
     */
    isCameraEnabled?: boolean;
    /**
     * Если видео готово к рендеру, то аватар и "загружаем видео" скрываются
     */
    isVideoReady?: boolean;
    /**
     * Ссылка на <video>
     */
    videoElementRef?: HTMLVideoElement | null;
    /**
     * Качество интернета участника
     */
    networkQualityType?: NetworkQualityType;
    /**
     * Ограничено ли видео у пользователя (передается причина вместо true)
     */
    videoLimitation?: MediaLimitationReason | false;
    /**
     * Индекс слота голума
     */
    slotIndex?: number;
}

export const ParticipantLayer: React.FC<Props> = ({
    userId,
    isVAD,
    isScreenSharing,
    isCameraEnabled,
    isVideoReady,
    videoElementRef,
    videoLimitation,
    networkQualityType,
    slotIndex,
}) => {
    const layerRef = useRef<HTMLDivElement>(null);
    const zoomControllerRef = useRef<SharingZoomController | null>(null);

    const isMobile = useSelector(isMobileUa);
    const activeGridView = useSelector(getActiveGridView);
    const isChatVisible = useSelector(isSidebarVisible);
    const desktopSharingStatus = useSelector(getDesktopSharingStatus);
    const isMeetingFullscreenEnabled = useSelector(selectIsMeetingFullscreenEnabled);

    const hideParticipantName = useSelector(selectHideParticipantName);
    const hideParticipantsVideo = useSelector(selectHideParticipantsVideo);
    const participantsCount = useSelector(getParticipantsCount);

    const participant = useTypedSelector((state) => getParticipant(state, userId));
    const isAudioOn = Boolean(participant.audioTrackState && !participant.audioTrackState.muted);
    const displayName = participant.displayName;
    const isRecordingActive = participant.isLocalRecording;

    const { shouldShowModerationMicrophone, shouldShowModerationPopupButton } = useShouldShowModerationControls(userId);
    const shouldShowModerationControls =
        (shouldShowModerationMicrophone || shouldShowModerationPopupButton) && activeGridView === GRID_VIEW.DEFAULT;
    const isPresenterView = activeGridView === GRID_VIEW.PRESENTER;

    const [shouldShowMoreButtonPopover, setShouldShowMoreButtonPopover] = useState(false);
    const toggleMoreButtonPopover = useCallback(() => {
        if (shouldShowModerationControls) {
            setShouldShowMoreButtonPopover(!shouldShowMoreButtonPopover);
        }
    }, [shouldShowMoreButtonPopover, shouldShowModerationControls]);
    const isTestplane = useSelector(selectIsTestplane);

    useEffect(() => {
        setShouldShowMoreButtonPopover(false);
    }, [participantsCount, isChatVisible]);

    useVideoOrientation(videoElementRef);

    const isSharingZoomEnabled = useSelector(selectIsSharingZoomEnabled);

    const isMainSlot =
        isScreenSharing ||
        (desktopSharingStatus === 'none' &&
            (slotIndex === N_LAST_OPTION.nCount - 1 || (isMobile && participantsCount === 1)));
    const isFullscreenButtonAvailable = isMeetingFullscreenEnabled || document.fullscreenEnabled || isMobile;
    const showFullscreenButton = shouldShowFullscreenButton({
        isFullscreenButtonAvailable,
        isMeetingFullscreenEnabled,
        isScreenSharing: Boolean(isScreenSharing),
    });

    const dispatch = useDispatch();

    useEffect(() => {
        if (!isSharingZoomEnabled || !isScreenSharing || !videoElementRef) {
            zoomControllerRef.current?.destroy();
            zoomControllerRef.current = null;
            return;
        }
        zoomControllerRef.current = new SharingZoomController(videoElementRef, dispatch);
        return () => {
            zoomControllerRef.current?.destroy();
            zoomControllerRef.current = null;
        };
    }, [videoElementRef, isScreenSharing, isSharingZoomEnabled, dispatch]);

    useEffect(() => {
        if (isCameraEnabled && !hideParticipantsVideo && !isScreenSharing && participant.id !== SELF) {
            dispatch(participantTurnedVideoOn(participant.id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCameraEnabled, isScreenSharing, hideParticipantsVideo]);

    useEffect(() => {
        if (!isVideoReady || participant.id === SELF || isScreenSharing) return;
        dispatch(participantTurnedVideoOnSuccess(participant.id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVideoReady, isScreenSharing]);

    useEffect(() => {
        if (!isVideoReady || !isScreenSharing) return;
        dispatch(participantScreenSharingSuccess());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVideoReady, isScreenSharing]);

    useEffect(() => {
        if (hideParticipantsVideo) {
            dispatch(participantTurnedVideoOff(participant.id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hideParticipantsVideo]);

    useEffect(() => {
        if (!isScreenSharing) {
            return () => {
                dispatch(participantTurnedVideoOff(participant.id));
            };
        }
    }, [participant.id, dispatch, isScreenSharing]);

    return (
        <div
            className={cn(styles.root, {
                [styles.rootPresenter]: isScreenSharing,
                [styles.rootModerator]: shouldShowModerationControls,
                [styles.rootModerationPopup]: shouldShowMoreButtonPopover,
                [styles.rootStroke]: isVAD && isAudioOn,
            })}
            ref={layerRef}
        >
            <div
                className={cn(styles.avatarWithNameBlock, {
                    [styles.withoutName]: hideParticipantName,
                })}
            >
                {isScreenSharing ? (
                    <ParticipantSharingPreloader displayName={displayName} isVideoReady={isVideoReady} />
                ) : (
                    <ParticipantAvatar isVideoOn={Boolean(isCameraEnabled && isVideoReady)} userId={userId} />
                )}
                {isSharingZoomEnabled && isScreenSharing && zoomControllerRef.current ? (
                    <SharingControlsPanel
                        controller={zoomControllerRef.current}
                        videoElementRef={videoElementRef}
                        showFullscreenButton={showFullscreenButton}
                    />
                ) : (
                    showFullscreenButton && <FullscreenButton videoElementRef={videoElementRef} />
                )}
                {!isTestplane ? (
                    <ParticipantVideoPreloader
                        isPresenterView={isPresenterView}
                        isCameraEnabled={isCameraEnabled && !isVideoReady && !videoLimitation}
                        isPSTNUser={participant.peerType === PeerType.INCOMING_PHONE_CALL}
                    />
                ) : null}
                <ParticipantName
                    className={styles.participantName}
                    userId={userId}
                    isAudioOn={isAudioOn}
                    activeGridView={activeGridView}
                    displayName={displayName}
                    isFromOverlay={false}
                    toggleMoreButtonPopup={toggleMoreButtonPopover}
                    shouldShowMoreButtonPopup={shouldShowMoreButtonPopover}
                    shouldShowModerationPopupButton={shouldShowModerationPopupButton}
                    shouldShowModerationControls={shouldShowModerationControls}
                    shouldShowModerationMicrophone={shouldShowModerationMicrophone}
                    isMobile={isMobile}
                    isRecordingActive={isRecordingActive}
                />
            </div>

            <ParticipantStatuses
                videoLimitation={!isVideoReady && videoLimitation}
                isBadNetworkQuality={networkQualityType === NetworkQualityType.BAD}
                participant={participant}
                showTooltip={!isMobile}
                isMainSlot={isMainSlot}
            />
            {isSharingZoomEnabled && isScreenSharing && videoElementRef && isVideoReady && (
                <SuspendedUseZoomPan target={videoElementRef} controllerRef={zoomControllerRef} />
            )}
        </div>
    );
};
